import { Suspense } from 'react';
import { Avatar, AvatarGroup, Box, Button, Stack, Typography } from '@mui/material';
import { CampaignOutlined, ArrowForwardRounded } from '@mui/icons-material';
import { Await, useNavigate, useParams } from 'react-router-dom';
import { OrderCustomer } from '@/services/customer.service';

/** 배너 색 토큰 (고객관리 화면과 동일 핑크 계열) */
const PINK = '#e84a8a';
const PINK_WEAK = '#fdeef5';
const PINK_STRONG = '#cf3a78';
const PINK_BORDER = '#f3a3c4';

interface Props {
    /** 로더의 customerSummary (defer) — { list, total } 로 resolve */
    summary: Promise<{ list: OrderCustomer[]; total: number }>;
}

const BannerShell = ({ children, onClick }: { children: React.ReactNode; onClick: () => void }) => (
    <Box
        onClick={onClick}
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1.5, sm: 2 },
            p: { xs: '16px', sm: '18px 20px' },
            mb: 2,
            borderRadius: '16px',
            // 부드러운 연핑크 배경 + 또렷한 핑크 보더(두껍게)
            background: 'linear-gradient(120deg, #fff3f9 0%, #ffffff 72%)',
            border: `2px solid ${PINK_BORDER}`,
            cursor: 'pointer',
            transition: 'box-shadow 0.18s ease, transform 0.12s ease, border-color 0.18s ease',
            '&:hover': {
                borderColor: PINK,
                boxShadow: '0 8px 22px rgba(232,74,138,.16)',
                transform: 'translateY(-1px)',
            },
            '&:active': { transform: 'translateY(0) scale(0.997)' },
        }}
    >
        {children}
    </Box>
);

/**
 * 판매자 정보 페이지 상단 "고객관리 바로가기" 배너.
 * 제목 아래에 노출되며, 클릭 시 고객관리(/customers)로 이동한다.
 * 로더의 customerSummary 로 전체 회원 수/최근 회원 아바타를 함께 보여준다.
 */
export const CustomerCareBanner = ({ summary }: Props) => {
    const navigate = useNavigate();
    const { mallId } = useParams();
    const goCustomers = () => navigate(`/mall/${mallId || '-'}/customers`);

    const IconBox = (
        <Box
            sx={{
                width: 46,
                height: 46,
                borderRadius: '13px',
                bgcolor: PINK_WEAK,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
            }}
        >
            <CampaignOutlined sx={{ fontSize: 25, color: PINK_STRONG }} />
        </Box>
    );

    const GoButton = (
        <Button
            onClick={(e) => {
                e.stopPropagation();
                goCustomers();
            }}
            endIcon={<ArrowForwardRounded sx={{ fontSize: 18 }} />}
            sx={{
                flexShrink: 0,
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 800,
                textTransform: 'none',
                borderRadius: '11px',
                px: '18px',
                py: '10px',
                bgcolor: PINK,
                color: '#fff',
                boxShadow: 'none',
                whiteSpace: 'nowrap',
                '&:hover': { bgcolor: PINK_STRONG, boxShadow: 'none' },
            }}
        >
            바로가기
        </Button>
    );

    return (
        <BannerShell onClick={goCustomers}>
            {IconBox}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 800, letterSpacing: '-0.02em', color: PINK_STRONG }}>
                        재구매 케어로 고객 관리하기
                    </Typography>
                    <Suspense fallback={null}>
                        <Await resolve={summary} errorElement={null}>
                            {(resolved: { list: OrderCustomer[]; total: number }) =>
                                resolved?.total > 0 ? (
                                    <Box
                                        component="span"
                                        sx={{
                                            fontSize: 11.5,
                                            fontWeight: 700,
                                            px: '9px',
                                            py: '2px',
                                            borderRadius: '20px',
                                            color: PINK_STRONG,
                                            bgcolor: PINK_WEAK,
                                        }}
                                    >
                                        전체 {resolved.total.toLocaleString()}명
                                    </Box>
                                ) : (
                                    <></>
                                )
                            }
                        </Await>
                    </Suspense>
                </Stack>
                <Typography sx={{ fontSize: 13, color: '#646c77', mt: '3px' }} noWrap>
                    재구매 유도 알림톡을 한 번에 보낼 수 있어요.
                </Typography>
            </Box>

            {/* 최근 회원 아바타 (데이터 있을 때만) */}
            <Suspense fallback={null}>
                <Await resolve={summary} errorElement={null}>
                    {(resolved: { list: OrderCustomer[]; total: number }) =>
                        resolved?.list?.length > 0 ? (
                            <AvatarGroup
                                max={4}
                                sx={{
                                    display: { xs: 'none', md: 'flex' },
                                    '& .MuiAvatar-root': {
                                        width: 32,
                                        height: 32,
                                        fontSize: 11,
                                        fontWeight: 700,
                                        bgcolor: '#e8eaed',
                                        color: '#6b7280',
                                        borderColor: '#fff',
                                    },
                                }}
                            >
                                {resolved.list.map((c) => (
                                    <Avatar key={c.id}>{c.realName?.slice(0, 2) || '?'}</Avatar>
                                ))}
                            </AvatarGroup>
                        ) : (
                            <></>
                        )
                    }
                </Await>
            </Suspense>

            {GoButton}
        </BannerShell>
    );
};

export default CustomerCareBanner;
